import { detectOverflow } from '@popperjs/core'
import type { Modifier } from '@popperjs/core'

import { getSide } from './arrow'

type FitHeightOptions = {
    // the distance in px the popover keeps from the edge of the screen
    padding?: number
}

/**
 * The height of a popover on the top or the bottom of the target that fits into the room it has there, and where it
 * starts: `excess` is the part of it that is outside of the screen (negative when there is room left). Opening above
 * the target, its bottom edge stays at the target and the top edge moves down.
 */
export const fitHeight = (
    side: 'top' | 'bottom',
    popper: { y: number; height: number },
    excess: number
): { y: number; height: number } => ({
    y: side === 'top' && excess > 0 ? popper.y + excess : popper.y,
    height: Math.max(0, popper.height - excess),
})

/**
 * Popper modifier: a popover on the top or the bottom of the target is not higher than the room it has there. It gets
 * this as `max-height`, so that the page can let its content scroll, and is moved right away to where it ends up.
 */
export const fitHeightModifier: Modifier<'fitHeight', FitHeightOptions> = {
    name: 'fitHeight',
    enabled: true,
    phase: 'main',
    requires: ['offset'],
    requiresIfExists: ['preventOverflow'],
    fn: ({ state, options }) => {
        const side = getSide(state.placement)
        const offsets = state.modifiersData.popperOffsets
        if (!offsets || (side !== 'top' && side !== 'bottom')) return

        const overflow = detectOverflow(state, { padding: options.padding })
        const fitted = fitHeight(side, { y: offsets.y, height: state.rects.popper.height }, overflow[side])

        offsets.y = fitted.y
        state.rects.popper = { ...state.rects.popper, height: fitted.height }
        state.styles.popper = { ...state.styles.popper, maxHeight: `${Math.floor(fitted.height)}px` }
    },
}
